import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { PDFDownloadLink } from "@react-pdf/renderer";
import Bill from "../components/Bill";

const ClientDetails = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRenewalModal, setShowRenewalModal] = useState(false);
    const [showMainRenewalModal, setShowMainRenewalModal] = useState(false);
    const [showQuickRenewalModal, setShowQuickRenewalModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingRenewal, setEditingRenewal] = useState(null);
    const [renewalStats, setRenewalStats] = useState(null);
    const [trainers, setTrainers] = useState([]);
    
    // Bill generation states
    const [showPDF, setShowPDF] = useState(false);
    const [logoImage, setLogoImage] = useState('');
    const [billData, setBillData] = useState(null);
    
    // Form states
    const [renewalForm, setRenewalForm] = useState({
        plan: '',
        start: '',
        end: '',
        amountPaid: '',
        balanceAmount: ''
    });

    const [mainRenewalForm, setMainRenewalForm] = useState({
        plan: '',
        startDate: '',
        endDate: '',
        customEndDate: '',
        amountPaid: '',
        balanceAmount: '',
        paymentMethod: ''
    });

    const [quickRenewalForm, setQuickRenewalForm] = useState({
        plan: '1',
        amountPaid: '',
        balanceAmount: '',
        extendFromCurrent: true
    });

    const [editForm, setEditForm] = useState({
        name: '',
        phonenumber: '',
        email: '',
        height: '',
        weight: '',
        paymentmethod: '',
        category: '',
        trainerId: '',
        balanceAmount: ''
    });

    useEffect(() => {
        fetchClientDetails();
        fetchRenewalStats();
        fetchTrainers();
    }, [clientId]);

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`https://klimkaara-backend-pgnq.onrender.com/clients/details/${clientId}`);
            
            if (response.status === 200) {
                setClient(response.data.client);
                setError(null);
            } else {
                setError(response.data.error || 'Failed to fetch client details');
                toast.error(response.data.error || 'Failed to fetch client details');
            }
        } catch (error) {
            console.error('Error fetching client details:', error);
            if (error.response && error.response.data && error.response.data.error) {
                setError(error.response.data.error);
                toast.error(error.response.data.error);
            } else {
                setError('Network error. Please try again.');
                toast.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchRenewalStats = async () => {
        try {
            const response = await axios.get(`https://klimkaara-backend-pgnq.onrender.com/clients/${clientId}/renewals`);
            if (response.status === 200) {
                setRenewalStats(response.data.statistics);
            }
        } catch (error) {
            console.error('Error fetching renewal stats:', error);
        }
    };

    const fetchTrainers = async () => {
        try {
            const response = await axios.get('https://klimkaara-backend-pgnq.onrender.com/trainers/getTrainers');
            if (response.status === 200) {
                setTrainers(response.data.trainers);
            }
        } catch (error) {
            console.error('Error fetching trainers:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateDaysRemaining = (endDate) => {
        if (!endDate) return 0;
        const today = new Date();
        const end = new Date(endDate);
        const diffTime = end - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Main renewal function - renews the client's current plan
    const handleMainRenewalSubmit = async (e) => {
        e.preventDefault();
        
        if (!mainRenewalForm.plan || mainRenewalForm.amountPaid === '') {
            toast.error('Plan and amount paid are required');
            return;
        }

        if (mainRenewalForm.plan === "None" && !mainRenewalForm.customEndDate) {
            toast.error('End date is required when plan is None');
            return;
        }

        try {
            const renewalData = {
                plan: mainRenewalForm.plan === "None" ? "None" : parseInt(mainRenewalForm.plan),
                startDate: mainRenewalForm.startDate || undefined,
                endDate: mainRenewalForm.plan === "None" ? mainRenewalForm.customEndDate : mainRenewalForm.endDate || undefined,
                amountPaid: parseFloat(mainRenewalForm.amountPaid) || 0,
                balanceAmount: parseFloat(mainRenewalForm.balanceAmount) || 0,
                paymentMethod: mainRenewalForm.paymentMethod || undefined
            };

            // Use the client's MongoDB _id for the main renewal endpoint
            const response = await axios.post(`https://klimkaara-backend-pgnq.onrender.com/clients/renew/${client._id}`, renewalData);

            if (response.status === 200) {
                toast.success('Client renewed successfully!');
                
                // Generate bill data for PDF
                const updatedClient = response.data.client;
                const billInfo = {
                    clientDetails: {
                        name: client.name,
                        phonenumber: client.phonenumber,
                        email: client.email
                    },
                    plan: mainRenewalForm.plan,
                    trainingType: client.category,
                    selectedTrainer: client.trainerName || '',
                    startDate: formatDate(updatedClient.start),
                    endDate: formatDate(updatedClient.end),
                    finalClientId: parseInt(client.clientId.split("-")[1]).toString(),
                    amountPaid: mainRenewalForm.amountPaid,
                    balanceAmount: mainRenewalForm.balanceAmount,
                    paymentMethod: mainRenewalForm.paymentMethod || client.paymentmethod
                };
                
                setBillData(billInfo);
                setLogoImage(response.data.base64 || '');
                setShowPDF(true);
                
                setShowMainRenewalModal(false);
                setMainRenewalForm({
                    plan: '',
                    startDate: '',
                    endDate: '',
                    customEndDate: '',
                    amountPaid: '',
                    balanceAmount: '',
                    paymentMethod: ''
                });
                fetchClientDetails();
                fetchRenewalStats();
            }
        } catch (error) {
            console.error('Error renewing client:', error);
            toast.error(error.response?.data?.error || 'Failed to renew client');
        }
    };

    // Quick renewal function
    const handleQuickRenewalSubmit = async (e) => {
        e.preventDefault();
        
        if (!quickRenewalForm.plan || quickRenewalForm.amountPaid === '') {
            toast.error('Plan and amount paid are required');
            return;
        }

        try {
            const renewalData = {
                plan: parseInt(quickRenewalForm.plan),
                amountPaid: parseFloat(quickRenewalForm.amountPaid) || 0,
                balanceAmount: parseFloat(quickRenewalForm.balanceAmount) || 0,
                extendFromCurrent: quickRenewalForm.extendFromCurrent
            };

            const response = await axios.post(`https://klimkaara-backend-pgnq.onrender.com/clients/${clientId}/quick-renewal`, renewalData);

            if (response.status === 200) {
                toast.success('Quick renewal completed successfully!');
                
                // Generate bill data for PDF
                const updatedClient = response.data.client;
                const billInfo = {
                    clientDetails: {
                        name: client.name,
                        phonenumber: client.phonenumber,
                        email: client.email
                    },
                    plan: quickRenewalForm.plan,
                    trainingType: client.category,
                    selectedTrainer: client.trainerName || '',
                    startDate: formatDate(updatedClient.start),
                    endDate: formatDate(updatedClient.end),
                    finalClientId: parseInt(client.clientId.split("-")[1]).toString(),
                    amountPaid: quickRenewalForm.amountPaid,
                    balanceAmount: quickRenewalForm.balanceAmount,
                    paymentMethod: client.paymentmethod
                };
                
                setBillData(billInfo);
                setLogoImage(response.data.base64 || '');
                setShowPDF(true);
                
                setShowQuickRenewalModal(false);
                setQuickRenewalForm({
                    plan: '1',
                    amountPaid: '',
                    balanceAmount: '',
                    extendFromCurrent: true
                });
                fetchClientDetails();
                fetchRenewalStats();
            }
        } catch (error) {
            console.error('Error in quick renewal:', error);
            toast.error(error.response?.data?.error || 'Failed to complete quick renewal');
        }
    };

    // Renewal history management (existing functionality)
    const handleRenewalSubmit = async (e) => {
        e.preventDefault();
        
        if (!renewalForm.plan || !renewalForm.start || !renewalForm.end || !renewalForm.amountPaid) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const renewalData = {
                ...renewalForm,
                amountPaid: parseFloat(renewalForm.amountPaid) || 0,
                balanceAmount: parseFloat(renewalForm.balanceAmount) || 0
            };

            let response;
            if (editingRenewal !== null) {
                response = await axios.put(`https://klimkaara-backend-pgnq.onrender.com/clients/${clientId}/renewals/${editingRenewal}`, renewalData);
            } else {
                response = await axios.post(`https://klimkaara-backend-pgnq.onrender.com/clients/${clientId}/renewals`, renewalData);
            }

            if (response.status === 200) {
                toast.success(editingRenewal !== null ? 'Renewal updated successfully' : 'Renewal added successfully');
                setShowRenewalModal(false);
                setEditingRenewal(null);
                setRenewalForm({
                    plan: '',
                    start: '',
                    end: '',
                    amountPaid: '',
                    balanceAmount: ''
                });
                fetchClientDetails();
                fetchRenewalStats();
            }
        } catch (error) {
            console.error('Error saving renewal:', error);
            toast.error(error.response?.data?.error || 'Failed to save renewal');
        }
    };

    const handleEditRenewal = (index) => {
        const renewal = client.renewals[index];
        setRenewalForm({
            plan: renewal.plan,
            start: new Date(renewal.start).toISOString().split('T')[0],
            end: new Date(renewal.end).toISOString().split('T')[0],
            amountPaid: renewal.amountPaid,
            balanceAmount: renewal.balanceAmount
        });
        setEditingRenewal(index);
        setShowRenewalModal(true);
    };

    const handleDeleteRenewal = async (index) => {
        if (window.confirm('Are you sure you want to delete this renewal?')) {
            try {
                const response = await axios.delete(`https://klimkaara-backend-pgnq.onrender.com/clients/${clientId}/renewals/${index}`);
                if (response.status === 200) {
                    toast.success('Renewal deleted successfully');
                    fetchClientDetails();
                    fetchRenewalStats();
                }
            } catch (error) {
                console.error('Error deleting renewal:', error);
                toast.error(error.response?.data?.error || 'Failed to delete renewal');
            }
        }
    };

    // Modal control functions
    const openRenewalModal = () => {
        setRenewalForm({
            plan: '',
            start: '',
            end: '',
            amountPaid: '',
            balanceAmount: ''
        });
        setEditingRenewal(null);
        setShowRenewalModal(true);
    };

    const openMainRenewalModal = () => {
        setMainRenewalForm({
            plan: '',
            startDate: '',
            endDate: '',
            customEndDate: '',
            amountPaid: '',
            balanceAmount: '',
            paymentMethod: client?.paymentmethod || ''
        });
        setShowMainRenewalModal(true);
    };

    const openQuickRenewalModal = () => {
        setQuickRenewalForm({
            plan: '1',
            amountPaid: '',
            balanceAmount: '',
            extendFromCurrent: !client?.planExpired
        });
        setShowQuickRenewalModal(true);
    };

    const closeAllModals = () => {
        setShowRenewalModal(false);
        setShowMainRenewalModal(false);
        setShowQuickRenewalModal(false);
        setShowEditModal(false);
        setShowPDF(false);
        setEditingRenewal(null);
        setBillData(null);
    };

    // Edit client functionality
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        
        if (!editForm.name || !editForm.phonenumber || !editForm.paymentmethod || !editForm.category) {
            toast.error('Name, phone number, payment method, and category are required');
            return;
        }

        try {
            const updateData = {
                name: editForm.name,
                phonenumber: editForm.phonenumber,
                email: editForm.email,
                height: editForm.height ? parseInt(editForm.height) : 0,
                weight: editForm.weight ? parseInt(editForm.weight) : 0,
                paymentmethod: editForm.paymentmethod,
                category: editForm.category,
                trainerId: editForm.category === 'Personal' ? editForm.trainerId : '',
                balanceAmount: parseFloat(editForm.balanceAmount) || 0
            };

            const response = await axios.put(`https://klimkaara-backend-pgnq.onrender.com/clients/update/${clientId}`, updateData);

            if (response.status === 200) {
                toast.success('Client details updated successfully!');
                setShowEditModal(false);
                fetchClientDetails();
            }
        } catch (error) {
            console.error('Error updating client:', error);
            toast.error(error.response?.data?.error || 'Failed to update client details');
        }
    };

    const openEditModal = () => {
        setEditForm({
            name: client.name || '',
            phonenumber: client.phonenumber || '',
            email: client.email || '',
            height: client.height || '',
            weight: client.weight || '',
            paymentmethod: client.paymentmethod || '',
            category: client.category || '',
            trainerId: client.trainerId || '',
            balanceAmount: client.balanceAmount || ''
        });
        setShowEditModal(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-lg">Loading client details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex justify-center items-center">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold mb-4">Error</h2>
                    <p className="text-gray-300 mb-6">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex justify-center items-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Client Not Found</h2>
                    <p className="text-gray-300 mb-6">The requested client could not be found.</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const daysRemaining = calculateDaysRemaining(client.end);
    const planStatus = client.planExpired ? 'Expired' : daysRemaining > 0 ? 'Active' : 'Expires Today';

    return (
        <div className="min-h-screen bg-gray-900 text-white py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-[#fded22]">Client Details</h1>
                        <p className="text-gray-400 mt-2">Detailed information for {client.name}</p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <button
                            onClick={openMainRenewalModal}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            🔄 Renew Plan
                        </button>
                        <button
                            onClick={openQuickRenewalModal}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            ⚡ Quick Renewal
                        </button>
                        <button
                            onClick={openRenewalModal}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            + Add History
                        </button>
                        <button
                            onClick={openEditModal}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            Edit Client
                        </button>
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                {/* Renewal Statistics Card */}
                {renewalStats && (
                    <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2 mb-4">
                                Renewal Statistics
                            </h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-blue-400">{renewalStats.totalRenewals}</p>
                                    <p className="text-gray-400 text-sm">Total Renewals</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-green-400">₹{renewalStats.totalAmountPaid}</p>
                                    <p className="text-gray-400 text-sm">Total Paid</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-red-400">₹{renewalStats.totalBalanceAmount}</p>
                                    <p className="text-gray-400 text-sm">Total Balance</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-yellow-400">₹{renewalStats.averageRenewalAmount.toFixed(2)}</p>
                                    <p className="text-gray-400 text-sm">Average Amount</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Client Information Card */}
                <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
                    {/* Status Banner */}
                    <div className={`p-4 text-center font-semibold ${
                        client.planExpired 
                            ? 'bg-red-600' 
                            : daysRemaining <= 7 
                                ? 'bg-yellow-600' 
                                : 'bg-green-600'
                    }`}>
                        Plan Status: {planStatus}
                        {!client.planExpired && daysRemaining > 0 && (
                            <span className="ml-2">({daysRemaining} days remaining)</span>
                        )}
                    </div>

                    <div className="p-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 gap-6 mb-8">
                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                    Basic Information
                                </h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-gray-400 text-sm">Client ID</label>
                                            <p className="text-white font-mono bg-gray-700 p-2 rounded">{parseInt(client.clientId.split("-")[1]).toString()}</p>
                                        </div>
                                        
                                        <div>
                                            <label className="text-gray-400 text-sm">Full Name</label>
                                            <p className="text-white font-semibold text-lg">{client.name}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-gray-400 text-sm">Phone Number</label>
                                            <p className="text-white">{client.phonenumber || 'N/A'}</p>
                                        </div>
                                        
                                        <div>
                                            <label className="text-gray-400 text-sm">Email</label>
                                            <p className="text-white">{client.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Plan Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                    Plan Information
                                </h2>
                                
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-gray-400 text-sm">Plan Duration</label>
                                        <p className="text-white">{client.plan === "None" ? "None" : `${client.plan} month(s)`}</p>
                                    </div>
                                    
                                    <div>
                                        <label className="text-gray-400 text-sm">Category</label>
                                        <p className={`text-white px-3 py-1 rounded-full text-sm inline-block ${
                                            client.category === 'Personal' ? 'bg-purple-600' : 'bg-blue-600'
                                        }`}>
                                            {client.category}
                                        </p>
                                    </div>
                                    
                                    <div>
                                        <label className="text-gray-400 text-sm">Start Date</label>
                                        <p className="text-white">{formatDate(client.start)}</p>
                                    </div>
                                    
                                    <div>
                                        <label className="text-gray-400 text-sm">End Date</label>
                                        <p className="text-white">{formatDate(client.end)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                    Physical Details
                                </h2>
                                
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-gray-400 text-sm">Height</label>
                                        <p className="text-white">{client.height ? `${client.height} cm` : 'Not specified'}</p>
                                    </div>
                                    
                                    <div>
                                        <label className="text-gray-400 text-sm">Weight</label>
                                        <p className="text-white">{client.weight ? `${client.weight} kg` : 'Not specified'}</p>
                                    </div>
                                    
                                    {client.height > 0 && client.weight > 0 && (
                                        <div>
                                            <label className="text-gray-400 text-sm">BMI</label>
                                            <p className="text-white">
                                                {((client.weight / ((client.height / 100) ** 2)).toFixed(2))}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Payment & Trainer Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                    Payment Information
                                </h2>
                                
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-gray-400 text-sm">Payment Method</label>
                                        <p className="text-white">{client.paymentmethod}</p>
                                    </div>
                                    
                                    <div>
                                        <label className="text-gray-400 text-sm">Balance Amount</label>
                                        <p className="text-white">₹{client.balanceAmount || 0}</p>
                                    </div>
                                </div>
                            </div>

                            {client.category === 'Personal' && (
                                <div className="space-y-4">
                                    <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                        Trainer Information
                                    </h2>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-gray-400 text-sm">Trainer Name</label>
                                            <p className="text-white">{client.trainerName || 'Not assigned'}</p>
                                        </div>
                                        
                                        {client.trainerPhone && (
                                            <div>
                                                <label className="text-gray-400 text-sm">Trainer Phone</label>
                                                <p className="text-white">{client.trainerPhone}</p>
                                            </div>
                                        )}
                                        
                                        <div>
                                            <label className="text-gray-400 text-sm">Trainer ID</label>
                                            <p className="text-white font-mono">{client.trainerId || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Renewals Section */}
                <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-[#fded22] border-b border-gray-700 pb-2">
                                Renewal History
                            </h2>
                        </div>

                        {client.renewals && client.renewals.length > 0 ? (
                            <div className="space-y-4">
                                {client.renewals.map((renewal, index) => (
                                    <div key={index} className="bg-gray-700 rounded-lg p-4 border-l-4 border-blue-500">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div>
                                                <label className="text-gray-400 text-sm">Plan Duration</label>
                                                <p className="text-white font-semibold">
                                                    {renewal.plan === "None" ? "None" : `${renewal.plan} month(s)`}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-gray-400 text-sm">Period</label>
                                                <p className="text-white">
                                                    {formatDate(renewal.start)} - {formatDate(renewal.end)}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-gray-400 text-sm">Amount Paid</label>
                                                <p className="text-green-400 font-semibold">₹{renewal.amountPaid}</p>
                                            </div>
                                            <div>
                                                <label className="text-gray-400 text-sm">Balance Amount</label>
                                                <p className="text-red-400 font-semibold">₹{renewal.balanceAmount}</p>
                                            </div>
                                            <div>
                                                <label className="text-gray-400 text-sm">Renewal Date</label>
                                                <p className="text-white">{formatDate(renewal.renewalDate)}</p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleEditRenewal(index)}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteRenewal(index)}
                                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-400">No renewals found for this client.</p>
                                <div className="mt-4 space-x-3">
                                    <button
                                        onClick={openMainRenewalModal}
                                        className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                                    >
                                        Renew Client Plan
                                    </button>
                                    <button
                                        onClick={openRenewalModal}
                                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
                                    >
                                        Add Renewal History
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Renewal Modal */}
            {showMainRenewalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-semibold text-[#fded22] mb-4">
                            🔄 Renew Client Plan
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                            This will update the client's main plan and add a renewal record to history.
                        </p>
                        
                        <form onSubmit={handleMainRenewalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Plan Duration (months) *</label>
                                <select
                                    value={mainRenewalForm.plan}
                                    onChange={(e) => setMainRenewalForm({...mainRenewalForm, plan: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    required
                                >
                                    <option value="">Select Plan</option>
                                    <option value="1">1 Month</option>
                                    <option value="3">3 Months</option>
                                    <option value="6">6 Months</option>
                                    <option value="12">12 Months</option>
                                    <option value="None">None</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Custom Start Date (optional)</label>
                                <input
                                    type="date"
                                    value={mainRenewalForm.startDate}
                                    onChange={(e) => setMainRenewalForm({...mainRenewalForm, startDate: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Leave empty to auto-calculate based on current plan status
                                </p>
                            </div>

                            {mainRenewalForm.plan === "None" && (
                                <div>
                                    <label className="block text-gray-400 text-sm mb-2">End Date *</label>
                                    <input
                                        type="date"
                                        value={mainRenewalForm.customEndDate}
                                        onChange={(e) => setMainRenewalForm({...mainRenewalForm, customEndDate: e.target.value})}
                                        className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                        min={mainRenewalForm.startDate || new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Amount Paid (₹) *</label>
                                <input
                                    type="number"
                                    value={mainRenewalForm.amountPaid}
                                    onChange={(e) => setMainRenewalForm({...mainRenewalForm, amountPaid: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    required
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Balance Amount (₹)</label>
                                <input
                                    type="number"
                                    value={mainRenewalForm.balanceAmount}
                                    onChange={(e) => setMainRenewalForm({...mainRenewalForm, balanceAmount: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Payment Method</label>
                                <select
                                    value={mainRenewalForm.paymentMethod}
                                    onChange={(e) => setMainRenewalForm({...mainRenewalForm, paymentMethod: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="">Keep Current ({client.paymentmethod})</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="Scan">Scan</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    type="button"
                                    onClick={closeAllModals}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                                >
                                    Renew Plan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Quick Renewal Modal */}
            {showQuickRenewalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-xl font-semibold text-[#fded22] mb-4">
                            ⚡ Quick Renewal
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                            Fast renewal with automatic date calculation.
                        </p>
                        
                        <form onSubmit={handleQuickRenewalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Plan Duration *</label>
                                <select
                                    value={quickRenewalForm.plan}
                                    onChange={(e) => setQuickRenewalForm({...quickRenewalForm, plan: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    required
                                >
                                    <option value="1">1 Month</option>
                                    <option value="3">3 Months</option>
                                    <option value="6">6 Months</option>
                                    <option value="12">12 Months</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Amount Paid (₹) *</label>
                                <input
                                    type="number"
                                    value={quickRenewalForm.amountPaid}
                                    onChange={(e) => setQuickRenewalForm({...quickRenewalForm, amountPaid: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    required
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Balance Amount (₹)</label>
                                <input
                                    type="number"
                                    value={quickRenewalForm.balanceAmount}
                                    onChange={(e) => setQuickRenewalForm({...quickRenewalForm, balanceAmount: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="extendFromCurrent"
                                    checked={quickRenewalForm.extendFromCurrent}
                                    onChange={(e) => setQuickRenewalForm({...quickRenewalForm, extendFromCurrent: e.target.checked})}
                                    className="mr-2"
                                    disabled={client.planExpired}
                                />
                                <label htmlFor="extendFromCurrent" className="text-gray-400 text-sm">
                                    Extend from current end date {client.planExpired ? '(Plan Expired)' : ''}
                                </label>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    type="button"
                                    onClick={closeAllModals}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
                                >
                                    Quick Renew
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Renewal History Modal (existing) */}
            {showRenewalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-xl font-semibold text-[#fded22] mb-4">
                            {editingRenewal !== null ? 'Edit Renewal History' : 'Add Renewal History'}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                            This adds/edits a renewal record in history without affecting the main plan.
                        </p>
                        
                        <form onSubmit={handleRenewalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Plan Duration (months) *</label>
                                <input
                                    type="number"
                                    value={renewalForm.plan}
                                    onChange={(e) => setRenewalForm({...renewalForm, plan: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    required
                                    min="1"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Start Date *</label>
                                <input
                                    type="date"
                                    value={renewalForm.start}
                                    onChange={(e) => setRenewalForm({...renewalForm, start: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">End Date *</label>
                                <input
                                    type="date"
                                    value={renewalForm.end}
                                    onChange={(e) => setRenewalForm({...renewalForm, end: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Amount Paid (₹) *</label>
                                <input
                                    type="number"
                                    value={renewalForm.amountPaid}
                                    onChange={(e) => setRenewalForm({...renewalForm, amountPaid: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    required
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Balance Amount (₹)</label>
                                <input
                                    type="number"
                                    value={renewalForm.balanceAmount}
                                    onChange={(e) => setRenewalForm({...renewalForm, balanceAmount: e.target.value})}
                                    className="w-full bg-gray-700 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    type="button"
                                    onClick={closeAllModals}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                >
                                    {editingRenewal !== null ? 'Update History' : 'Add to History'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Client Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg w-full max-w-lg relative max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b border-gray-700">
                            <h2 className="text-2xl font-bold text-yellow-400">
                                Edit Client Details
                            </h2>
                            <button
                                onClick={closeAllModals}
                                className="text-gray-500 hover:text-white transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6">
                            <form onSubmit={handleEditSubmit} className="space-y-4">
                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Name</span>
                                        <input
                                            type="text"
                                            placeholder="Enter client name"
                                            required
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                        />
                                    </label>
                                </div>

                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Phone Number</span>
                                        <input
                                            type="text"
                                            placeholder="Enter phone number"
                                            required
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                            value={editForm.phonenumber}
                                            onChange={(e) => setEditForm({...editForm, phonenumber: e.target.value})}
                                        />
                                    </label>
                                </div>

                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Email ID</span>
                                        <input
                                            type="email"
                                            placeholder="Enter email address"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                        />
                                    </label>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block">
                                            <span className="text-yellow-400 font-medium mb-2 block">Height (cms)</span>
                                            <input
                                                type="number"
                                                placeholder="Height"
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                                value={editForm.height}
                                                onChange={(e) => setEditForm({...editForm, height: e.target.value})}
                                                step="1"
                                            />
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block">
                                            <span className="text-yellow-400 font-medium mb-2 block">Weight (kgs)</span>
                                            <input
                                                type="number"
                                                placeholder="Weight"
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                                value={editForm.weight}
                                                onChange={(e) => setEditForm({...editForm, weight: e.target.value})}
                                                step="1"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Payment Method</span>
                                        <select
                                            value={editForm.paymentmethod}
                                            onChange={(e) => setEditForm({...editForm, paymentmethod: e.target.value})}
                                            required
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                        >
                                            <option value="">Select method</option>
                                            <option value="Scan">Scan</option>
                                            <option value="Cash">Cash</option>
                                            <option value="Card">Card</option>
                                        </select>
                                    </label>
                                </div>

                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Type of Training</span>
                                        <select
                                            value={editForm.category}
                                            onChange={(e) => setEditForm({...editForm, category: e.target.value, trainerId: e.target.value === 'General' ? '' : editForm.trainerId})}
                                            required
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                        >
                                            <option value="">Select training type</option>
                                            <option value="General">General Training</option>
                                            <option value="Personal">Personal Training</option>
                                        </select>
                                    </label>
                                </div>

                                {editForm.category === 'Personal' && (
                                    <div>
                                        <label className="block">
                                            <span className="text-yellow-400 font-medium mb-2 block">Trainer</span>
                                            <select
                                                value={editForm.trainerId}
                                                onChange={(e) => setEditForm({...editForm, trainerId: e.target.value})}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                                required={editForm.category === 'Personal'}
                                            >
                                                <option value="" disabled>Select a trainer</option>
                                                {trainers.map((trainer) => (
                                                    <option key={trainer.trainerId} value={trainer.trainerId}>
                                                        {trainer.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                )}

                                <div>
                                    <label className="block">
                                        <span className="text-yellow-400 font-medium mb-2 block">Balance Amount</span>
                                        <input
                                            type="text"
                                            placeholder="Enter balance amount"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                                            value={editForm.balanceAmount}
                                            onChange={(e) => setEditForm({...editForm, balanceAmount: e.target.value})}
                                        />
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-yellow-400 text-black font-bold p-3 rounded-lg hover:bg-yellow-500 transition-colors"
                                >
                                    Update Client
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Download Modal */}
            {showPDF && billData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-xl font-semibold text-[#fded22] mb-4">
                            📄 Renewal Bill Generated
                        </h3>
                        <p className="text-gray-400 text-sm mb-6">
                            Your renewal bill has been generated successfully. Click below to download the PDF.
                        </p>
                        
                        <div className="space-y-4">
                            <PDFDownloadLink
                                document={
                                    <Bill
                                        clientDetails={billData.clientDetails}
                                        trainingType={billData.trainingType}
                                        plan={billData.plan}
                                        selectedTrainer={billData.selectedTrainer}
                                        logoImageBase64={logoImage}
                                        finalClientId={billData.finalClientId}
                                        startDate={billData.startDate}
                                        endDate={billData.endDate}
                                        amountPaid={billData.amountPaid}
                                        balanceAmount={billData.balanceAmount}
                                    />
                                }
                                fileName={`${billData.clientDetails.name}_Renewal_Bill.pdf`}
                                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg text-center block transition-colors"
                            >
                                {({ blob, url, loading, error }) =>
                                    loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Generating PDF...
                                        </div>
                                    ) : (
                                        "📥 Download Renewal Bill"
                                    )
                                }
                            </PDFDownloadLink>
                            
                            <button
                                onClick={closeAllModals}
                                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientDetails; 